// Is it getting worse, and for whom.
//
// Twelve months at most and six lines at most, because that is what a reader can
// tell apart and what the operation returns. Each line carries the change
// against the month before, so nobody subtracts two figures off the screen and
// gets the sign the wrong way round.
//
// THE MARKERS ARE THE SECOND FACT. A solid marker is a month that has been
// billed and can never move again; a hollow one is a month that still can. A
// line that mixes them says which part of itself is settled, which is what
// somebody comparing two months is really asking.

import { useMemo, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import type { Currency, UsageTrendResponse, UsageTrendSeries } from "@crm/shared";
import styles from "./UsagePage.module.css";
import { formatUsageMoney } from "./usageFormat.js";
import { isNonprod, resolveDeployments, writeStoredDeployments } from "./deploymentSelection.js";
import { bindingsInForce, deploymentsOf, tenantsOf } from "./usageScope.js";
import {
  periodToUsageSearch,
  rangeStart,
  searchToComparison,
  searchToMonthCount,
  searchToSlugs,
  searchToUsagePeriod,
  slugsToSearch,
  TREND_MONTH_COUNTS,
  USAGE_COMPARISONS,
  type UsageComparison,
  type UsageTrendSearch,
} from "./usageSearch.js";
import { latestUsageFx } from "./usageMerge.js";
import { Card, CardBody, CardHeader } from "../../design/Card.js";
import { EmptyState, ProblemMessage, Spinner } from "../../design/Feedback.js";
import { FilterSelect, SelectionChips } from "../../design/Filters.js";
import { QueryBoundary } from "../../design/QueryBoundary.js";
import { Table, Td, Th } from "../../design/Table.js";
import { TrendLines, type TrendSeriesLine } from "../../design/chart/TrendLines.js";
import { colorForKey, labelForKey, MAX_SERIES } from "../../design/chart/series.js";
import { usePageChrome } from "../../design/pageChrome.js";
import { useDeal, useTenantBindings, useUsageTrend } from "../../api/queries.js";
import { formatInstantDate, formatMonth, todayIsoDate } from "../../format/calendarDate.js";
import { formatCount, formatPercent } from "../../format/money.js";

const COMPARE_OPTIONS = USAGE_COMPARISONS.map((value) => ({
  value,
  label: value === "tenant_slug" ? "tenants" : value === "deployment_slug" ? "deployments" : value,
}));

const RANGE_OPTIONS = TREND_MONTH_COUNTS.map((count) => ({
  value: String(count),
  label: `${count} months`,
}));

export function UsageTrendPage(): ReactNode {
  const { dealId } = useParams({ from: "/deals/$dealId/usage/trend" });
  const deal = useDeal(dealId);
  const bindings = useTenantBindings(dealId);

  usePageChrome({
    title: deal.data === undefined ? "Usage trend" : `${deal.data.customer_name} · Usage trend`,
  });

  return (
    <div className={styles.page}>
      <div className={styles.breadcrumb}>
        <Link to="/deals">Deals</Link>
        <span aria-hidden="true">/</span>
        <Link to="/deals/$dealId" params={{ dealId }}>
          {deal.data?.customer_name ?? "Deal"}
        </Link>
        <span aria-hidden="true">/</span>
        <Link to="/deals/$dealId/usage" params={{ dealId }}>
          Usage
        </Link>
        <span aria-hidden="true">/</span>
        <span className={styles.crumbCurrent}>Trend</span>
      </div>

      <QueryBoundary query={bindings} label="this deal's tenants">
        {(list) =>
          list.items.length === 0 ? (
            <EmptyState
              title="No tenant is bound to this deal."
              description="A trend is one line per tenant, and this deal has none bound to it yet."
            />
          ) : deal.data === undefined ? (
            // The figures come back in the deal's currency, so the reads wait
            // for the deal rather than ask twice.
            <Spinner label="Loading the deal…" />
          ) : (
            <TrendBody dealId={dealId} bindings={list.items} currency={deal.data.currency} />
          )
        }
      </QueryBoundary>
    </div>
  );
}

interface TrendBodyProps {
  readonly dealId: string;
  readonly bindings: Parameters<typeof bindingsInForce>[0];
  /** The deal's currency: one deal's page reads in the deal's money. */
  readonly currency: Currency;
}

function TrendBody({ dealId, bindings, currency }: TrendBodyProps): ReactNode {
  const search = useSearch({ from: "/deals/$dealId/usage/trend" });
  const navigate = useNavigate();
  const today = todayIsoDate();
  const periodTo = searchToUsagePeriod(search, today);
  const months = searchToMonthCount(search);
  const compare = searchToComparison(search);
  const periodFrom = rangeStart(periodTo, months);

  // The whole range, not one month: a tenant bound in June belongs on a line
  // that runs from April, and dropping it would make the line start late.
  const inForce = useMemo(
    () => bindings.filter((binding) => binding.effective_from.slice(0, 7) <= periodTo),
    [bindings, periodTo],
  );
  const availableDeployments = useMemo(() => deploymentsOf(inForce), [inForce]);
  const tenants = useMemo(() => tenantsOf(inForce), [inForce]);
  const deployments = resolveDeployments({
    dealId,
    available: availableDeployments,
    fromUrl: searchToSlugs(search.deployments),
  });

  const results = useUsageTrend(
    deployments.map((deploymentSlug) => ({
      periodFrom,
      periodTo,
      deploymentSlug,
      tenantSlugs: tenants.slice(0, MAX_SERIES),
      groupBy: compare,
      limit: MAX_SERIES,
      currency,
    })),
  );

  const update = (next: Partial<UsageTrendSearch>): void => {
    void navigate({
      to: "/deals/$dealId/usage/trend",
      params: { dealId },
      search: (previous: UsageTrendSearch) => ({ ...previous, ...next }),
    });
  };

  const toggleDeployment = (slug: string): void => {
    const next = deployments.includes(slug)
      ? deployments.filter((value) => value !== slug)
      : [...availableDeployments].filter((value) => value === slug || deployments.includes(value));
    if (next.length === 0) return;
    writeStoredDeployments(dealId, next);
    update({ deployments: slugsToSearch(next) });
  };

  const failed = results.find((result) => result.error !== null)?.error ?? null;
  const reports = results.flatMap((result) => (result.data === undefined ? [] : [result.data]));
  const merged = reports.length === 0 ? null : mergeTrend(reports, currency);

  return (
    <>
      <SelectionChips
        label="Deployments"
        options={availableDeployments.map((slug) => ({
          value: slug,
          label: slug,
          cautioned: isNonprod(slug),
        }))}
        selected={deployments}
        onToggle={toggleDeployment}
      />

      <div className={styles.controls}>
        <FilterSelect
          label="Compare"
          options={COMPARE_OPTIONS}
          value={compare}
          onChange={(next: UsageComparison) => update({ compare: next })}
        />
        <FilterSelect
          label="Range"
          options={RANGE_OPTIONS}
          value={String(months)}
          onChange={(next) => update({ months: Number(next) })}
        />
        <span>{`${formatMonth(periodFrom)} – ${formatMonth(periodTo)}`}</span>
        <Link
          to="/deals/$dealId/usage"
          params={{ dealId }}
          search={{
            period: periodToUsageSearch(periodTo, today),
            deployments: slugsToSearch(deployments),
          }}
        >
          One month →
        </Link>
      </div>

      {failed !== null && <ProblemMessage error={failed} action="load the trend" />}

      {merged === null ? (
        results.some((result) => result.isPending) ? (
          <Spinner label="Loading the trend…" />
        ) : (
          <EmptyState
            title="No month of this range has been rolled up."
            description="A trend reads the rollup the worker filled; it never prices a month itself. Open a single month and refresh it to fill one."
          />
        )
      ) : (
        <TrendFigures report={merged} dealId={dealId} deployments={deployments} />
      )}
    </>
  );
}

interface TrendFiguresProps {
  readonly report: UsageTrendResponse;
  readonly dealId: string;
  readonly deployments: readonly string[];
}

function TrendFigures({ report, dealId, deployments }: TrendFiguresProps): ReactNode {
  if (report.series.length === 0) {
    return (
      <EmptyState
        title="Nothing in this range cost anything."
        description="Every month of it is rolled up and every figure is zero, which means no fact matched an active meter."
      />
    );
  }

  const closed = new Set(report.closed_periods);
  const lines: TrendSeriesLine[] = report.series.map((line, index) => ({
    key: line.key,
    label: labelForKey(line.key),
    color: colorForKey(line.key, index),
    points: line.points.map((point) => ({
      period: point.period,
      value: point.cost_minor,
      closed: closed.has(point.period),
    })),
  }));

  return (
    <>
      <Card>
        <CardHeader
          title="Cost by month"
          description="A solid marker is a month that has been billed and cannot move. A hollow one is a month that still can."
        />
        <CardBody>
          <TrendLines
            periods={report.periods}
            series={lines}
            formatValue={(value) => formatUsageMoney(value, report.currency)}
            formatPeriod={(period) => formatMonth(period).slice(0, 3)}
            label="Usage cost by month"
            emptyMessage="No month of this range has a priced fact."
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="The range, line by line"
          description="The change is the last month against the month before it. A range of one month has no change to report."
        />
        <Table label="Usage cost per series">
          <thead>
            <tr>
              <Th>Series</Th>
              <Th numeric>Last month</Th>
              <Th numeric>Change</Th>
              <Th numeric>Range total</Th>
              <Th numeric>Calls</Th>
              <Th numeric>Per call</Th>
            </tr>
          </thead>
          <tbody>
            {report.series.map((line) => (
              <SeriesRow key={line.key} line={line} currency={report.currency} />
            ))}
          </tbody>
        </Table>
      </Card>

      {report.series_omitted.count > 0 && (
        <div className={styles.footer}>
          {`${formatCount(report.series_omitted.count)} more series are not drawn, worth ${formatUsageMoney(report.series_omitted.cost_minor, report.currency)}. Six lines is what a reader can tell apart.`}
        </div>
      )}

      <div className={styles.footer}>
        <span>
          {report.computed_at === null
            ? "No rollup row stands behind this range yet."
            : `Rolled up to ${formatInstantDate(report.computed_at)}. A month is refreshed from its own screen.`}
        </span>
        <span className={styles.footerSpacer} />
        <Link
          to="/deals/$dealId/usage"
          params={{ dealId }}
          search={{ deployments: slugsToSearch(deployments) }}
        >
          Back to the month →
        </Link>
      </div>
    </>
  );
}

function SeriesRow({
  line,
  currency,
}: {
  readonly line: UsageTrendSeries;
  readonly currency: string;
}): ReactNode {
  const last = line.points[line.points.length - 1];
  const previous = line.points[line.points.length - 2];
  const delta = line.delta_vs_previous_minor;
  const calls = last?.calls ?? null;
  return (
    <tr>
      <Td>{labelForKey(line.key)}</Td>
      <Td numeric>{formatUsageMoney(last?.cost_minor ?? 0, currency)}</Td>
      <Td numeric>
        {delta === null || previous === undefined || previous.cost_minor === 0 ? (
          "—"
        ) : (
          <span
            className={`${styles.delta} ${delta > 0 ? styles.deltaUp : delta < 0 ? styles.deltaDown : ""}`}
          >
            {`${delta > 0 ? "+" : ""}${formatPercent((delta / previous.cost_minor) * 100, 1)}`}
          </span>
        )}
      </Td>
      <Td numeric>{formatUsageMoney(line.cost_minor, currency)}</Td>
      {/* Null, not zero: a call has one tenant and no one model, so a per-model
          call count would be a number nobody could act on. */}
      <Td numeric>{calls === null ? "—" : formatCount(calls)}</Td>
      <Td numeric>
        {calls === null || calls === 0 || last === undefined
          ? "—"
          : formatUsageMoney(last.cost_minor / calls, currency)}
      </Td>
    </tr>
  );
}

/**
 * One trend out of one per deployment.
 *
 * The rollup rows behind two deployments are disjoint, so a series that appears
 * on both is summed point by point. `closed_periods` INTERSECTS rather than
 * unions: a month is only settled when every deployment in view has billed it.
 */
function mergeTrend(
  reports: readonly UsageTrendResponse[],
  currency: Currency,
): UsageTrendResponse {
  const first = reports[0];
  if (first === undefined || reports.length === 1) {
    return (
      first ?? {
        period_from: "",
        period_to: "",
        periods: [],
        closed_periods: [],
        currency,
        fx: null,
        group_by: "tenant_slug",
        series: [],
        series_total: 0,
        series_omitted: { count: 0, cost_minor: 0 },
        computed_at: null,
      }
    );
  }

  const periods = first.periods;
  const byKey = new Map<
    string,
    Map<string, { cost: number; calls: number | null; unpriced: number }>
  >();
  for (const report of reports) {
    for (const line of report.series) {
      const points = byKey.get(line.key) ?? new Map();
      for (const point of line.points) {
        const existing = points.get(point.period);
        points.set(point.period, {
          cost: (existing?.cost ?? 0) + point.cost_minor,
          calls:
            point.calls === null ? (existing?.calls ?? null) : (existing?.calls ?? 0) + point.calls,
          unpriced: (existing?.unpriced ?? 0) + point.facts_unpriced,
        });
      }
      byKey.set(line.key, points);
    }
  }

  const series: UsageTrendSeries[] = [...byKey.entries()]
    .map(([key, points]) => {
      const drawn = periods.map((period) => {
        const bucket = points.get(period);
        return {
          period,
          cost_minor: bucket?.cost ?? 0,
          calls: bucket?.calls ?? null,
          facts_unpriced: bucket?.unpriced ?? 0,
        };
      });
      const last = drawn[drawn.length - 1];
      const previous = drawn[drawn.length - 2];
      return {
        key,
        cost_minor: drawn.reduce((sum, point) => sum + point.cost_minor, 0),
        delta_vs_previous_minor:
          last === undefined || previous === undefined
            ? null
            : last.cost_minor - previous.cost_minor,
        points: drawn,
      };
    })
    .sort((left, right) => right.cost_minor - left.cost_minor || left.key.localeCompare(right.key));

  return {
    ...first,
    fx: latestUsageFx(reports.map((report) => report.fx)),
    series: series.slice(0, MAX_SERIES),
    series_total: series.length,
    series_omitted: {
      count: Math.max(series.length - MAX_SERIES, 0),
      cost_minor: series.slice(MAX_SERIES).reduce((sum, line) => sum + line.cost_minor, 0),
    },
    closed_periods: periods.filter((period) =>
      reports.every((report) => report.closed_periods.includes(period)),
    ),
    computed_at:
      reports
        .map((report) => report.computed_at)
        .filter((at): at is string => at !== null)
        .sort()[0] ?? null,
  };
}
